import {
  Table,
  Empty,
  Spin,
  Typography,
  Space,
  Tag,
  Tooltip,
  Button,
  Popconfirm,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  Dropdown,
  Image,
  Grid,
  Segmented,
} from "antd";
import type { MenuProps } from "antd";

const { useBreakpoint } = Grid;
import {
  FileOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SelectOutlined,
  DownOutlined,
  EyeOutlined,
  DownloadOutlined,
  LoadingOutlined,
  UsergroupAddOutlined,
  AppstoreOutlined,
  BarsOutlined,
} from "@ant-design/icons";
import type { FilterDropdownProps } from "antd/es/table/interface";
import { RelationshipCardView } from "./RelationshipCardView";
import { CreateRelatedModal } from "@/components/CreateRelatedModal";
import { ColumnSettings } from "@/components/ColumnSettings";
import { BooleanCheckboxCell } from "./BooleanCheckboxCell";
import { BooleanColumnHeader } from "./BooleanColumnHeader";
import { useTranslate, useNavigation, useNotification } from "@refinedev/core";
import { useRelationshipItems } from "@/hooks/useRelationshipItems";
import { useRelationshipMutations } from "@/hooks/useRelationshipMutations";
import { useRelationshipGridColumns } from "@/hooks/useRelationshipGridColumns";
import { usePopulateRelationship } from "@/hooks/usePopulateRelationship";
import { useColumnPreferences } from "@/hooks/useColumnPreferences";
import { useListValues } from "@/hooks/useListValues";
import { ItemPickerModal, type PickedItem } from "@/components/ItemPickerModal";
import { resolveDisplayValue } from "@/utils/displayUtils";
import type {
  RelationshipMetadata,
  RelationshipItemData,
  RelationshipGridColumn,
  GridColumnMetadata,
} from "@/types";
import { useState, useMemo, useEffect } from "react";

const { Text, Link } = Typography;

/**
 * Convert snake_case to camelCase
 * Aras property names use snake_case, but our API returns camelCase
 */
const snakeToCamel = (str: string): string => {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
};

interface RelationshipTableProps {
  /** The source ItemType name */
  itemType: string;
  /** The source item ID */
  itemId: string;
  /** The relationship metadata */
  relationship: RelationshipMetadata;
  /** Whether this tab is currently active */
  isActive: boolean;
}

/**
 * Table component for displaying relationship items with CRUD operations
 */
export const RelationshipTable: React.FC<RelationshipTableProps> = ({
  itemType,
  itemId,
  relationship,
  isActive,
}) => {
  const translate = useTranslate();
  const { show } = useNavigation();
  const { open: openNotification } = useNotification();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [pagination, setPagination] = useState({ page: 1, pageSize: 10 });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<RelationshipItemData | null>(
    null,
  );
  const [selectedItem, setSelectedItem] = useState<PickedItem | null>(null);
  const [form] = Form.useForm();

  // Preview modal state for File relationships
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewFile, setPreviewFile] = useState<{
    id: string;
    name: string;
    mimeType?: string;
  } | null>(null);
  const [previewBlobUrl, setPreviewBlobUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Loading state for individual boolean cells (key: `${rowId}_${columnName}`)
  const [loadingCells, setLoadingCells] = useState<Map<string, boolean>>(
    new Map(),
  );
  // Loading state for bulk column operations (key: columnName)
  const [bulkLoadingColumns, setBulkLoadingColumns] = useState<
    Map<string, boolean>
  >(new Map());

  // Fetch file with auth headers and create blob URL for preview
  useEffect(() => {
    if (!previewFile || !previewVisible) {
      // Cleanup blob URL when modal closes
      if (previewBlobUrl) {
        URL.revokeObjectURL(previewBlobUrl);
        setPreviewBlobUrl(null);
      }
      return;
    }

    const fetchFileForPreview = async () => {
      setPreviewLoading(true);
      try {
        const token = localStorage.getItem("access_token");
        const response = await fetch(`/api/files/${previewFile.id}/view`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        setPreviewBlobUrl(blobUrl);
      } catch (error) {
        console.error("Failed to fetch file for preview:", error);
        setPreviewBlobUrl(null);
      } finally {
        setPreviewLoading(false);
      }
    };

    fetchFileForPreview();

    // Cleanup on unmount or when previewFile changes
    return () => {
      if (previewBlobUrl) {
        URL.revokeObjectURL(previewBlobUrl);
      }
    };
  }, [previewFile?.id, previewVisible]);

  // Fetch all relationship items at once for client-side filtering & pagination.
  // Relationships typically have <200 items (e.g., students in a class).
  const { data, isLoading, isError, refetch } = useRelationshipItems({
    itemType,
    itemId,
    relationshipName: relationship.name,
    page: 1,
    pageSize: 500,
    enabled: isActive,
  });

  // Fetch grid column metadata from API
  const { data: gridColumnsData, isLoading: gridColumnsLoading } =
    useRelationshipGridColumns({
      relationshipName: relationship.name,
      enabled: isActive,
    });

  // Convert grid columns to GridColumnMetadata format for column preferences
  const allGridColumns: GridColumnMetadata[] = useMemo(
    () =>
      (gridColumnsData?.columns || []).map((col) => ({
        name: col.key,
        label: col.label,
        dataType: col.dataType,
        sortOrder: col.sortOrder,
        width: col.width,
        alignment: col.alignment,
        isRequired: col.isRequired,
      })),
    [gridColumnsData],
  );

  const {
    visibleColumns: visibleGridColumns,
    preferences: columnPrefs,
    toggleColumn,
    resetToDefault,
  } = useColumnPreferences({
    resource: `rel:${relationship.name}`,
    defaultColumns: allGridColumns,
    maxDefaultVisible: 8,
  });

  // Extract list names from grid columns for list value translation
  const listColumnNames = useMemo(() => {
    if (!gridColumnsData?.columns) return [];
    return gridColumnsData.columns
      .filter(
        (col) =>
          (col.dataType === "list" || col.dataType === "filter list") &&
          col.dataSource,
      )
      .map((col) => col.dataSource as string);
  }, [gridColumnsData?.columns]);

  // Get editable columns with full metadata for form rendering
  // Only columns from "relationship" source that are not read-only
  const editableColumns = useMemo(() => {
    if (!gridColumnsData?.columns) return [];
    return gridColumnsData.columns.filter(
      (col) => col.source === "relationship" && !col.isReadOnly,
    );
  }, [gridColumnsData?.columns]);

  // Detect boolean-heavy relationships (e.g., attendance) for card view
  const booleanColumns = useMemo(() => {
    if (!gridColumnsData?.columns) return [];
    return gridColumnsData.columns.filter(
      (col) =>
        col.dataType === "boolean" &&
        col.source === "relationship" &&
        !col.isReadOnly,
    );
  }, [gridColumnsData?.columns]);

  const nonBooleanRelColumns = useMemo(() => {
    if (!gridColumnsData?.columns) return [];
    return gridColumnsData.columns.filter(
      (col) =>
        col.dataType !== "boolean" &&
        col.source === "relationship" &&
        !col.isReadOnly,
    );
  }, [gridColumnsData?.columns]);

  const isBooleanHeavy =
    booleanColumns.length >= 2 &&
    editableColumns.length > 0 &&
    booleanColumns.length / editableColumns.length >= 0.5;

  type RelViewMode = "table" | "card";
  const [manualViewMode, setManualViewMode] = useState<RelViewMode | null>(
    null,
  );
  const viewMode: RelViewMode =
    manualViewMode ?? (isMobile && isBooleanHeavy ? "card" : "table");

  // Fetch list values for list-type columns
  const { lists: listValues, isLoading: listsLoading } = useListValues({
    listNames: listColumnNames,
    enabled: isActive && listColumnNames.length > 0,
  });

  const {
    addRelationshipItem,
    updateRelationshipItem,
    deleteRelationshipItem,
    bulkAddRelationshipItems,
    isAddLoading,
    isUpdateLoading,
    isDeleteLoading,
  } = useRelationshipMutations();

  // Populate from reference field (e.g., "Populate from Class")
  const {
    populateSource,
    isPopulating,
    getPreSelectedIds,
    populateFromItems,
  } = usePopulateRelationship({
    itemType,
    itemId,
    relationship,
    existingItems: data?.items ?? [],
    enabled: isActive && !!relationship.relatedItemTypeName,
  });

  // State for the populate picker modal (separate from the "Add Existing" picker)
  const [isPopulatePickerOpen, setIsPopulatePickerOpen] = useState(false);
  const [populatePreSelectedIds, setPopulatePreSelectedIds] = useState<
    string[]
  >([]);

  // Handle "Populate from" click - open picker for source items
  const handlePopulateClick = async () => {
    const preSelected = await getPreSelectedIds();
    setPopulatePreSelectedIds(preSelected);
    setIsPopulatePickerOpen(true);
  };

  // Handle selection from populate picker
  const handlePopulateSelect = async (items: PickedItem[]) => {
    setIsPopulatePickerOpen(false);
    if (items.length === 0) return;
    const ids = items.map((item) => item.id);
    const success = await populateFromItems(ids);
    if (success) {
      refetch();
    }
  };

  // Handle "Add Existing" - open the item picker first
  const handleAddExisting = () => {
    setEditingItem(null);
    setSelectedItem(null);
    form.resetFields();
    // If we have a related item type, open the picker first
    if (relationship.relatedItemTypeName) {
      setIsPickerOpen(true);
    } else {
      // Fallback to direct modal for relationships without related item type
      setIsModalOpen(true);
    }
  };

  // Handle "Create New" - open the create modal
  const handleCreateNew = () => {
    setIsCreateModalOpen(true);
  };

  // Handle created item from CreateRelatedModal
  const handleItemCreated = async (
    newItemId: string,
    _newItemData: Record<string, unknown>,
  ) => {
    // Close the create modal
    setIsCreateModalOpen(false);

    // Always link the relationship immediately after creating the item
    // This prevents orphaned items when the user cancels the properties modal
    try {
      await addRelationshipItem({
        itemType,
        itemId,
        relationshipName: relationship.name,
        relatedItemId: newItemId,
        properties: {},
      });
      refetch();
    } catch {
      // addRelationshipItem already shows error notification via useCustomMutation
    }
  };

  // Dropdown menu items for Add button
  const addMenuItems: MenuProps["items"] = [
    {
      key: "addExisting",
      label: translate("buttons.addExisting", "Add Existing"),
      icon: <SelectOutlined />,
      onClick: handleAddExisting,
    },
    {
      key: "createNew",
      label: translate("buttons.createNew", "Create New"),
      icon: <PlusOutlined />,
      onClick: handleCreateNew,
    },
    // Conditionally add "Populate from" option when a source is discovered
    ...(populateSource
      ? [
          { type: "divider" as const },
          {
            key: "populateFromReference",
            label: isPopulating
              ? translate("buttons.populating", "Populating...")
              : translate(
                  "buttons.populateFrom",
                  { source: populateSource.label },
                  `Populate from ${populateSource.label}`,
                ),
            icon: isPopulating ? <LoadingOutlined /> : <UsergroupAddOutlined />,
            onClick: handlePopulateClick,
            disabled: isPopulating,
          },
        ]
      : []),
  ];

  // Handle item selection from picker (supports multi-select)
  const handleItemSelect = async (items: PickedItem[]) => {
    if (items.length === 0) return;

    setIsPickerOpen(false);

    if (items.length === 1) {
      // Single item: open properties modal as before
      setSelectedItem(items[0]);
      setIsModalOpen(true);
    } else {
      // Multiple items: bulk-add without properties modal
      try {
        await bulkAddRelationshipItems({
          itemType,
          itemId,
          relationshipName: relationship.name,
          items: items.map((item) => ({ relatedId: item.id })),
        });
        openNotification?.({
          message: translate(
            "notifications.bulkAddSuccess",
            { count: items.length },
            `${items.length} items added successfully`,
          ),
          type: "success",
        });
        refetch();
      } catch {
        // Error notification is handled by the mutation hook
      }
    }
  };

  // Handle Edit button click
  const handleEdit = (record: RelationshipItemData) => {
    setEditingItem(record);
    form.setFieldsValue({
      ...record.relationshipProperties,
      relatedId: record.relatedId,
    });
    setIsModalOpen(true);
  };

  // Handle Delete button click
  const handleDelete = async (record: RelationshipItemData) => {
    await deleteRelationshipItem({
      itemType,
      itemId,
      relationshipName: relationship.name,
      relationshipItemId: record.id,
    });
    refetch();
  };

  /**
   * Handle individual boolean checkbox toggle
   */
  const handleBooleanToggle = async (
    record: RelationshipItemData,
    columnName: string,
    newValue: boolean,
  ) => {
    const cellKey = `${record.id}_${columnName}`;

    // Set loading state
    setLoadingCells((prev) => new Map(prev).set(cellKey, true));

    try {
      // Convert boolean to Aras format ('1' or '0')
      await updateRelationshipItem({
        itemType,
        itemId,
        relationshipName: relationship.name,
        relationshipItemId: record.id,
        properties: { [columnName]: newValue ? "1" : "0" },
        // Simple notification with i18n support
        successNotification: {
          message: translate(
            "notifications.editSuccess",
            "Successfully updated",
          ),
          type: "success",
        },
        errorNotification: {
          message: translate("notifications.editError", "Error when updating"),
          type: "error",
        },
      });
      refetch();
    } catch (error) {
      // Error notification is handled by the mutation hook
      console.error("Failed to update boolean field:", error);
    } finally {
      setLoadingCells((prev) => {
        const next = new Map(prev);
        next.delete(cellKey);
        return next;
      });
    }
  };

  /**
   * Handle check all / uncheck all for a boolean column
   */
  const handleBulkBooleanToggle = async (
    columnName: string,
    newValue: boolean,
  ) => {
    if (!data?.items || data.items.length === 0) return;

    setBulkLoadingColumns((prev) => new Map(prev).set(columnName, true));

    // Find items that need to be updated
    const itemsToUpdate = data.items.filter((item) => {
      const currentValue =
        item.relationshipProperties?.[snakeToCamel(columnName)];
      // Convert to boolean for comparison
      const isTruthy =
        currentValue === true || currentValue === "1" || currentValue === 1;
      return isTruthy !== newValue;
    });

    if (itemsToUpdate.length === 0) {
      setBulkLoadingColumns((prev) => {
        const next = new Map(prev);
        next.delete(columnName);
        return next;
      });
      return;
    }

    try {
      // Update all items in parallel, suppress individual notifications
      await Promise.all(
        itemsToUpdate.map((item) =>
          updateRelationshipItem({
            itemType,
            itemId,
            relationshipName: relationship.name,
            relationshipItemId: item.id,
            properties: { [columnName]: newValue ? "1" : "0" },
            successNotification: false, // Suppress individual notifications
            errorNotification: false,
          }),
        ),
      );
      refetch();
      // Show single success notification
      openNotification?.({
        type: "success",
        message: translate("notifications.editSuccess", "Successfully updated"),
      });
    } catch (error) {
      console.error("Bulk update failed:", error);
      // Show error notification
      openNotification?.({
        type: "error",
        message: translate("notifications.editError", "Error when updating"),
      });
    } finally {
      setBulkLoadingColumns((prev) => {
        const next = new Map(prev);
        next.delete(columnName);
        return next;
      });
    }
  };

  // Handle modal form submission
  const handleModalSubmit = async () => {
    try {
      const values = await form.validateFields();
      const properties = { ...values };

      if (editingItem) {
        // Update existing
        await updateRelationshipItem({
          itemType,
          itemId,
          relationshipName: relationship.name,
          relationshipItemId: editingItem.id,
          properties,
        });
      } else {
        // Add new - use selected item from picker
        const relatedId = selectedItem?.id;
        if (!relatedId && relationship.relatedItemTypeName) {
          // Should not happen if picker is working correctly
          return;
        }
        await addRelationshipItem({
          itemType,
          itemId,
          relationshipName: relationship.name,
          relatedItemId: relatedId,
          properties,
        });
      }

      setIsModalOpen(false);
      setSelectedItem(null);
      form.resetFields();
      refetch();
    } catch {
      // Form validation error - do nothing
    }
  };

  // Handle modal cancel
  const handleModalCancel = () => {
    setIsModalOpen(false);
    setSelectedItem(null);
    form.resetFields();
  };

  // Build columns from metadata (preferred) or data (fallback)
  const columns = useMemo(() => {
    // Check if this is a File relationship
    const isFileRelationship =
      relationship.relatedItemTypeName === "File" ||
      relationship.name.toLowerCase().includes("file") ||
      relationship.name.toLowerCase().includes("attachment");

    // Column type definition
    type TableColumn = {
      title: string | React.ReactNode;
      dataIndex: string[];
      key: string;
      width?: number;
      align?: "left" | "center" | "right";
      render?: (
        value: unknown,
        record: RelationshipItemData,
      ) => React.ReactNode;
      filterDropdown?: (props: FilterDropdownProps) => React.ReactNode;
      onFilter?: (value: unknown, record: RelationshipItemData) => boolean;
    };

    // Helper function to create render function based on data type
    const createRenderer = (
      col: RelationshipGridColumn,
    ):
      | ((value: unknown, record: RelationshipItemData) => React.ReactNode)
      | undefined => {
      // Convert snake_case to camelCase for consistent comparison
      const propName = snakeToCamel(col.name);

      // Special handling for name/keyed_name columns - make them clickable links
      if (
        col.source === "related" &&
        (propName === "name" || propName === "keyedName")
      ) {
        return (value: unknown, record: RelationshipItemData) => {
          const relatedTypeName = relationship.relatedItemTypeName;
          if (relatedTypeName && record.relatedId) {
            return (
              <Link onClick={() => show(relatedTypeName, record.relatedId!)}>
                {String(value || "")}
              </Link>
            );
          }
          return <Text>{String(value || "")}</Text>;
        };
      }

      // Filename with icon
      if (propName === "filename") {
        return (value: unknown) => (
          <Space>
            <FileOutlined />
            <Text>{String(value || "")}</Text>
          </Space>
        );
      }

      // File size formatting
      if (propName === "fileSize") {
        return (value: unknown) => {
          if (typeof value !== "number") return null;
          return formatFileSize(value);
        };
      }

      // State with tag
      if (propName === "state" || propName === "stateLabel") {
        return (value: unknown) => {
          if (!value) return null;
          return <Tag>{String(value)}</Tag>;
        };
      }

      // Date formatting
      if (col.dataType === "date" || col.dataType === "datetime") {
        return (value: unknown) => {
          if (!value) return null;
          return new Date(String(value)).toLocaleDateString();
        };
      }

      // Boolean formatting - interactive checkbox for editable columns
      if (col.dataType === "boolean") {
        // Check if this column is editable:
        // - Must be from relationship source (not related)
        // - Must not be marked as read-only
        const isEditable = col.source === "relationship" && !col.isReadOnly;

        return (value: unknown, record: RelationshipItemData) => {
          if (value === null || value === undefined) return null;

          // Convert to boolean (Aras stores as '1'/'0' or true/false)
          const checked =
            value === true || value === "1" || value === 1 || value === "true";

          if (isEditable) {
            const cellKey = `${record.id}_${col.name}`;
            const isLoading =
              loadingCells.get(cellKey) || bulkLoadingColumns.get(col.name);

            return (
              <BooleanCheckboxCell
                checked={checked}
                loading={isLoading}
                disabled={bulkLoadingColumns.get(col.name)}
                onChange={(newValue) =>
                  handleBooleanToggle(record, col.name, newValue)
                }
                ariaLabel={`${col.label} for row ${record.id}`}
              />
            );
          }

          // Read-only: show text
          return checked ? "Yes" : "No";
        };
      }

      // Item reference - display human-readable name from {propName}Display field
      // Backend returns both ID (e.g., fileType) and display name (e.g., fileTypeDisplay)
      // Uses resolveDisplayValue to filter out GUIDs and item_number codes
      if (col.dataType === "item") {
        return (value: unknown, record: RelationshipItemData) => {
          if (!value) return null;

          // Try to get display value from {propName}Display field
          const displayKey = propName + "Display";
          const primarySource =
            col.source === "related"
              ? record.relatedItemProperties
              : record.relationshipProperties;
          const fallbackSource =
            col.source === "related"
              ? record.relationshipProperties
              : record.relatedItemProperties;

          const displayValue =
            primarySource?.[displayKey] || fallbackSource?.[displayKey];

          const resolved = resolveDisplayValue(value, displayValue);

          return (
            <Tooltip title={resolved}>
              <Text
                ellipsis
                style={{ maxWidth: col.width ? col.width - 20 : 120 }}
              >
                {resolved}
              </Text>
            </Tooltip>
          );
        };
      }

      // List value translation - translate raw value to label
      if (
        (col.dataType === "list" || col.dataType === "filter list") &&
        col.dataSource
      ) {
        return (value: unknown) => {
          if (!value) return null;
          const rawValue = String(value);
          // Look up the label from list values
          const listVals = listValues[col.dataSource!];
          if (listVals) {
            const listItem = listVals.find((v) => v.value === rawValue);
            if (listItem?.label) {
              return (
                <Tooltip title={listItem.label}>
                  <Text
                    ellipsis
                    style={{ maxWidth: col.width ? col.width - 20 : 100 }}
                  >
                    {listItem.label}
                  </Text>
                </Tooltip>
              );
            }
          }
          // Fallback to raw value if no translation found
          return <Text>{rawValue}</Text>;
        };
      }

      // Long text with tooltip (comments, description, etc.)
      if (
        propName === "comments" ||
        propName === "description" ||
        col.dataType === "text"
      ) {
        return (value: unknown) => (
          <Tooltip title={String(value || "")}>
            <Text
              ellipsis
              style={{ maxWidth: col.width ? col.width - 20 : 180 }}
            >
              {String(value || "")}
            </Text>
          </Tooltip>
        );
      }

      return undefined;
    };

    // Helper to extract the display text of a column for filtering
    const getColumnText = (
      col: RelationshipGridColumn,
      record: RelationshipItemData,
    ): string => {
      const propName = snakeToCamel(col.name);
      const primarySource =
        col.source === "related"
          ? record.relatedItemProperties
          : record.relationshipProperties;
      const fallbackSource =
        col.source === "related"
          ? record.relationshipProperties
          : record.relatedItemProperties;

      let value = primarySource?.[propName];
      if (value === undefined || value === null || value === "") {
        value = fallbackSource?.[propName];
      }
      if (value === undefined || value === null || value === "") return "";

      // Item reference: use display field
      if (col.dataType === "item") {
        const displayKey = propName + "Display";
        const displayValue =
          primarySource?.[displayKey] || fallbackSource?.[displayKey];
        const resolved = resolveDisplayValue(value, displayValue);
        return resolved === "-" ? "" : resolved;
      }

      // List: translate value to label
      if (
        (col.dataType === "list" || col.dataType === "filter list") &&
        col.dataSource
      ) {
        const listVals = listValues[col.dataSource];
        if (listVals) {
          const listItem = listVals.find((v) => v.value === String(value));
          if (listItem?.label) return listItem.label;
        }
        return String(value);
      }

      return String(value);
    };

    // Build filter dropdown and onFilter for a column
    const buildColumnFilter = (
      col: RelationshipGridColumn,
    ): Pick<TableColumn, "filterDropdown" | "onFilter"> => {
      // Skip boolean columns — they have check-all header instead
      if (col.dataType === "boolean") return {};

      // List/filter list columns: use Select dropdown
      if (
        (col.dataType === "list" || col.dataType === "filter list") &&
        col.dataSource
      ) {
        const listVals = listValues[col.dataSource] || [];
        if (listVals.length > 0) {
          return {
            filterDropdown: ({
              setSelectedKeys,
              selectedKeys,
              confirm,
              clearFilters,
            }: FilterDropdownProps) => (
              <div style={{ padding: 8 }}>
                <Select
                  style={{ width: 200, marginBottom: 8, display: "block" }}
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  value={selectedKeys[0] as string}
                  onChange={(val) =>
                    setSelectedKeys(val ? [val] : [])
                  }
                  placeholder={translate("table.selectFilter", "Chọn...")}
                  options={listVals.map((lv) => ({
                    label: lv.label || lv.value,
                    value: lv.value,
                  }))}
                />
                <Space>
                  <Button
                    type="primary"
                    onClick={() => confirm()}
                    size="small"
                    style={{ width: 90 }}
                  >
                    {translate("buttons.filter", "Lọc")}
                  </Button>
                  <Button
                    onClick={() => {
                      clearFilters?.();
                      confirm();
                    }}
                    size="small"
                    style={{ width: 90 }}
                  >
                    {translate("buttons.clear", "Xóa")}
                  </Button>
                </Space>
              </div>
            ),
            onFilter: (value: unknown, record: RelationshipItemData) => {
              const propName = snakeToCamel(col.name);
              const primarySource =
                col.source === "related"
                  ? record.relatedItemProperties
                  : record.relationshipProperties;
              const fallbackSource =
                col.source === "related"
                  ? record.relationshipProperties
                  : record.relatedItemProperties;
              let cellValue = primarySource?.[propName];
              if (cellValue === undefined || cellValue === null) {
                cellValue = fallbackSource?.[propName];
              }
              return String(cellValue) === String(value);
            },
          };
        }
      }

      // Text-based filter for string, text, item, date, integer, etc.
      return {
        filterDropdown: ({
          setSelectedKeys,
          selectedKeys,
          confirm,
          clearFilters,
        }: FilterDropdownProps) => (
          <div style={{ padding: 8 }}>
            <Input
              value={selectedKeys[0] as string}
              onChange={(e) =>
                setSelectedKeys(e.target.value ? [e.target.value] : [])
              }
              onPressEnter={() => confirm()}
              placeholder={translate("table.searchName", "Tìm kiếm...")}
              style={{ width: 200, marginBottom: 8, display: "block" }}
            />
            <Space>
              <Button
                type="primary"
                onClick={() => confirm()}
                size="small"
                style={{ width: 90 }}
              >
                {translate("buttons.filter", "Lọc")}
              </Button>
              <Button
                onClick={() => {
                  clearFilters?.();
                  confirm();
                }}
                size="small"
                style={{ width: 90 }}
              >
                {translate("buttons.clear", "Xóa")}
              </Button>
            </Space>
          </div>
        ),
        onFilter: (value: unknown, record: RelationshipItemData) => {
          const text = getColumnText(col, record);
          return text.toLowerCase().includes(String(value).toLowerCase());
        },
      };
    };

    // Helper function to build columns from metadata
    const buildColumnsFromMetadata = (
      gridColumns: RelationshipGridColumn[],
    ): TableColumn[] => {
      const cols: TableColumn[] = [];

      for (const col of gridColumns) {
        // Convert snake_case property name to camelCase (Aras uses snake_case, API returns camelCase)
        const propName = snakeToCamel(col.name);

        // Determine the data path based on source
        const dataIndex: string[] =
          col.source === "related"
            ? ["relatedItemProperties", propName]
            : ["relationshipProperties", propName];

        // Get custom renderer if any
        const customRenderer = createRenderer(col);

        // Create a smart render function that:
        // 1. Tries the primary source location first
        // 2. Falls back to the other location if not found
        // This handles cases where data might be saved to relationship but metadata expects related
        const smartRender = (
          _value: unknown,
          record: RelationshipItemData,
        ): React.ReactNode => {
          // Get value from primary location
          const primarySource =
            col.source === "related"
              ? record.relatedItemProperties
              : record.relationshipProperties;
          const fallbackSource =
            col.source === "related"
              ? record.relationshipProperties
              : record.relatedItemProperties;

          let value = primarySource?.[propName];

          // If not found in primary, try fallback (e.g., comments saved to relationship but metadata says related)
          if (value === undefined || value === null || value === "") {
            value = fallbackSource?.[propName];
          }

          // Use custom renderer if available
          if (customRenderer) {
            return customRenderer(value, record);
          }

          // Default: just display the value
          if (value === undefined || value === null || value === "") {
            return null;
          }
          return <Text>{String(value)}</Text>;
        };

        // For boolean editable columns, add custom header with check all
        const isEditableBoolean =
          col.dataType === "boolean" &&
          col.source === "relationship" &&
          !col.isReadOnly;

        let columnTitle: string | React.ReactNode = col.label;

        if (isEditableBoolean && data?.items) {
          const allValues = data.items.map((item) => {
            const val = item.relationshipProperties?.[snakeToCamel(col.name)];
            return val === true || val === "1" || val === 1 || val === "true";
          });
          const checkedCount = allValues.filter(Boolean).length;
          const allChecked =
            checkedCount === allValues.length && allValues.length > 0;
          const someChecked =
            checkedCount > 0 && checkedCount < allValues.length;

          columnTitle = (
            <BooleanColumnHeader
              title={col.label}
              allChecked={allChecked}
              someChecked={someChecked}
              loading={bulkLoadingColumns.get(col.name)}
              onToggleAll={(checked) =>
                handleBulkBooleanToggle(col.name, checked)
              }
            />
          );
        }

        cols.push({
          title: columnTitle,
          dataIndex,
          key: col.key,
          width: col.width,
          align: col.alignment as "left" | "center" | "right",
          render: smartRender,
          ...buildColumnFilter(col),
        });
      }

      return cols;
    };

    // Helper function to build columns from data (fallback)
    const buildColumnsFromData = (): TableColumn[] => {
      if (!data?.items || data.items.length === 0) {
        return [];
      }

      const firstItem = data.items[0];
      const relatedProps = firstItem.relatedItemProperties || {};
      const relProps = firstItem.relationshipProperties || {};
      const cols: TableColumn[] = [];

      // Add related item name/keyed_name first if available
      if (relatedProps["keyedName"] || relatedProps["name"]) {
        cols.push({
          title: translate("fields.name", "Name"),
          dataIndex: [
            "relatedItemProperties",
            relatedProps["keyedName"] ? "keyedName" : "name",
          ],
          key: "name",
          render: (value: unknown, record: RelationshipItemData) => {
            const relatedTypeName = relationship.relatedItemTypeName;
            if (relatedTypeName && record.relatedId) {
              return (
                <Link onClick={() => show(relatedTypeName, record.relatedId!)}>
                  {String(value || "")}
                </Link>
              );
            }
            return <Text>{String(value || "")}</Text>;
          },
        });
      }

      // Add item_number if available
      if (relatedProps["itemNumber"]) {
        cols.push({
          title: translate("fields.itemNumber", "Item Number"),
          dataIndex: ["relatedItemProperties", "itemNumber"],
          key: "itemNumber",
          width: 150,
        });
      }

      // Add state if available
      if (relatedProps["state"]) {
        cols.push({
          title: translate("fields.state", "State"),
          dataIndex: ["relatedItemProperties", "state"],
          key: "state",
          width: 120,
          render: (value: unknown) => {
            if (!value) return null;
            return <Tag>{String(value)}</Tag>;
          },
        });
      }

      // For File relationships, add special columns
      if (isFileRelationship) {
        if (relatedProps["filename"]) {
          cols.push({
            title: translate("fields.filename", "Filename"),
            dataIndex: ["relatedItemProperties", "filename"],
            key: "filename",
            render: (value: unknown) => (
              <Space>
                <FileOutlined />
                <Text>{String(value || "")}</Text>
              </Space>
            ),
          });
        }
        if (relatedProps["fileSize"]) {
          cols.push({
            title: translate("fields.fileSize", "Size"),
            dataIndex: ["relatedItemProperties", "fileSize"],
            key: "fileSize",
            width: 100,
            render: (value: unknown) => {
              if (typeof value !== "number") return null;
              return formatFileSize(value);
            },
          });
        }
      }

      // Add comments if available on relationship
      if (relProps["comments"]) {
        cols.push({
          title: translate("fields.comments", "Comments"),
          dataIndex: ["relationshipProperties", "comments"],
          key: "comments",
          width: 200,
          render: (value: unknown) => (
            <Tooltip title={String(value || "")}>
              <Text ellipsis style={{ maxWidth: 180 }}>
                {String(value || "")}
              </Text>
            </Tooltip>
          ),
        });
      }

      // Add created_on if available
      if (relatedProps["createdOn"]) {
        cols.push({
          title: translate("fields.createdOn", "Created"),
          dataIndex: ["relatedItemProperties", "createdOn"],
          key: "createdOn",
          width: 150,
          render: (value: unknown) => {
            if (!value) return null;
            return new Date(String(value)).toLocaleDateString();
          },
        });
      }

      return cols;
    };

    // Build data columns: prefer metadata, fallback to data analysis
    // Filter by column visibility preferences
    const visibleColumnKeys = new Set(visibleGridColumns.map((c) => c.name));
    let dataColumns: TableColumn[];
    if (gridColumnsData?.columns && gridColumnsData.columns.length > 0) {
      const filteredGridColumns = gridColumnsData.columns.filter((col) =>
        visibleColumnKeys.has(col.key),
      );
      dataColumns = buildColumnsFromMetadata(filteredGridColumns);

      // For File relationships, ensure filename column is included even if not in metadata
      // This handles cases where filename property is hidden in Aras but should be shown
      if (isFileRelationship) {
        const hasFilename = dataColumns.some((col) => col.key === "filename");
        if (
          !hasFilename &&
          data?.items?.[0]?.relatedItemProperties?.["filename"]
        ) {
          // Insert filename column at the beginning (after name/keyedName if exists)
          const nameColIndex = dataColumns.findIndex(
            (col) =>
              col.key === "name" ||
              col.key === "keyedName" ||
              col.key === "name_R" ||
              col.key === "keyedName_R",
          );
          const filenameCol: TableColumn = {
            title: translate("fields.filename", "Filename"),
            dataIndex: ["relatedItemProperties", "filename"],
            key: "filename",
            render: (value: unknown) => (
              <Space>
                <FileOutlined />
                <Text>{String(value || "")}</Text>
              </Space>
            ),
          };
          if (nameColIndex >= 0) {
            dataColumns.splice(nameColIndex + 1, 0, filenameCol);
          } else {
            dataColumns.unshift(filenameCol);
          }
        }
      }
    } else {
      dataColumns = buildColumnsFromData();
    }

    // Always add Actions column at the end
    // Responsive width: smaller on mobile
    const actionsWidth = isMobile
      ? isFileRelationship
        ? 120
        : 70
      : isFileRelationship
        ? 140
        : 90;

    const actionsColumn: TableColumn = {
      title: translate("table.actions", "Actions"),
      dataIndex: [],
      key: "actions",
      width: actionsWidth,
      align: "center",
      render: (_: unknown, record: RelationshipItemData) => {
        const relatedProps = record.relatedItemProperties || {};
        const mimeType = relatedProps.mimetype as string | undefined;
        const filename =
          (relatedProps.filename as string) ||
          (relatedProps.keyedName as string) ||
          "file";
        const fileId = record.relatedId;

        return (
          <Space size="small">
            {/* View button for previewable files */}
            {isFileRelationship && fileId && (
              <Tooltip title={translate("buttons.view", "View")}>
                <Button
                  type="text"
                  size="small"
                  icon={<EyeOutlined />}
                  onClick={() => {
                    setPreviewFile({
                      id: fileId,
                      name: filename,
                      mimeType: mimeType,
                    });
                    setPreviewVisible(true);
                  }}
                />
              </Tooltip>
            )}
            {/* Download button for File relationships */}
            {isFileRelationship && fileId && (
              <Tooltip title={translate("buttons.download", "Download")}>
                <Button
                  type="text"
                  size="small"
                  icon={<DownloadOutlined />}
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem("access_token");
                      const response = await fetch(
                        `/api/files/${fileId}/download`,
                        {
                          headers: { Authorization: `Bearer ${token}` },
                        },
                      );
                      if (!response.ok) throw new Error("Download failed");
                      const blob = await response.blob();
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = filename;
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      URL.revokeObjectURL(url);
                    } catch (error) {
                      console.error("Download failed:", error);
                    }
                  }}
                />
              </Tooltip>
            )}
            <Tooltip title={translate("buttons.edit", "Edit")}>
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleEdit(record)}
              />
            </Tooltip>
            <Popconfirm
              title={translate(
                "buttons.confirm",
                "Are you sure you want to delete this item?",
              )}
              onConfirm={() => handleDelete(record)}
              okText={translate("buttons.yes", "Yes")}
              cancelText={translate("buttons.no", "No")}
            >
              <Tooltip title={translate("buttons.delete", "Delete")}>
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  loading={isDeleteLoading}
                />
              </Tooltip>
            </Popconfirm>
          </Space>
        );
      },
    };

    return [...dataColumns, actionsColumn];
  }, [
    data,
    gridColumnsData,
    listValues,
    relationship,
    translate,
    show,
    isDeleteLoading,
    handleEdit,
    handleDelete,
    isMobile,
    loadingCells,
    bulkLoadingColumns,
    handleBooleanToggle,
    handleBulkBooleanToggle,
    visibleGridColumns,
  ]);

  if (isError) {
    return (
      <Empty
        description={translate("errors.fetchFailed", "Failed to load data")}
      />
    );
  }

  if (isLoading || gridColumnsLoading || listsLoading) {
    return (
      <div style={{ textAlign: "center", padding: "40px" }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: isMobile ? 12 : 16,
        }}
      >
        {relationship.relatedItemTypeName ? (
          <Dropdown.Button
            type="primary"
            icon={<DownOutlined />}
            menu={{ items: addMenuItems }}
            onClick={handleAddExisting}
          >
            <PlusOutlined /> {translate("buttons.add", "Add")}
          </Dropdown.Button>
        ) : (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddExisting}
          >
            {translate("buttons.add", "Add")}
          </Button>
        )}
        <Space>
          {isBooleanHeavy && (
            <Segmented
              value={viewMode}
              onChange={(v) => setManualViewMode(v as RelViewMode)}
              options={[
                { value: "card", icon: <AppstoreOutlined /> },
                { value: "table", icon: <BarsOutlined /> },
              ]}
              size={isMobile ? "small" : "middle"}
            />
          )}
          {viewMode === "table" && allGridColumns.length > 0 && (
            <ColumnSettings
              columns={allGridColumns}
              hiddenColumns={columnPrefs.hiddenColumns}
              onToggleColumn={toggleColumn}
              onResetToDefault={resetToDefault}
            />
          )}
        </Space>
      </div>

      {!data?.items || data.items.length === 0 ? (
        <Empty
          description={translate("common.noItems", "No items found")}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      ) : viewMode === "card" ? (
        <RelationshipCardView
          items={data.items}
          allItems={data.items}
          relationship={relationship}
          booleanColumns={booleanColumns}
          nonBooleanColumns={nonBooleanRelColumns}
          loadingCells={loadingCells}
          bulkLoadingColumns={bulkLoadingColumns}
          onBooleanToggle={handleBooleanToggle}
          onBulkBooleanToggle={handleBulkBooleanToggle}
          onEdit={handleEdit}
          onDelete={handleDelete}
          isDeleteLoading={isDeleteLoading}
          pagination={pagination}
          totalCount={data.items.length}
          onPaginationChange={(page, pageSize) =>
            setPagination({ page, pageSize })
          }
          isMobile={isMobile}
        />
      ) : (
        <Table
          dataSource={data.items}
          columns={columns.length > 0 ? columns : defaultColumns}
          rowKey="id"
          size="small"
          scroll={{ x: "max-content" }}
          pagination={{
            current: pagination.page,
            pageSize: pagination.pageSize,
            showSizeChanger: !isMobile,
            size: isMobile ? "small" : "default",
            showTotal: isMobile
              ? undefined
              : (total) =>
                  translate("common.totalItems", { total }, `${total} items`),
            onChange: (page, pageSize) => setPagination({ page, pageSize }),
          }}
        />
      )}

      {/* Item Picker Modal (Add Existing) */}
      {relationship.relatedItemTypeName && (
        <ItemPickerModal
          itemTypeName={relationship.relatedItemTypeName}
          open={isPickerOpen}
          onCancel={() => setIsPickerOpen(false)}
          onSelect={handleItemSelect}
          multiSelect
        />
      )}

      {/* Populate Picker Modal (select source items, e.g., multiple Classes) */}
      {populateSource && (
        <ItemPickerModal
          itemTypeName={populateSource.referencedItemType}
          open={isPopulatePickerOpen}
          onCancel={() => setIsPopulatePickerOpen(false)}
          onSelect={handlePopulateSelect}
          multiSelect
          selectedIds={populatePreSelectedIds}
          title={translate(
            "buttons.populateFrom",
            { source: populateSource.label },
            `Populate from ${populateSource.label}`,
          )}
        />
      )}

      {/* Add/Edit Properties Modal */}
      <Modal
        title={
          editingItem
            ? translate("buttons.edit", "Edit") +
              " " +
              (relationship.tabLabel || relationship.name)
            : translate("buttons.add", "Add") +
              " " +
              (relationship.tabLabel || relationship.name)
        }
        open={isModalOpen}
        onOk={handleModalSubmit}
        onCancel={handleModalCancel}
        confirmLoading={isAddLoading || isUpdateLoading}
      >
        <Form form={form} layout="vertical">
          {/* Display selected item (for new relationships) */}
          {!editingItem && selectedItem && (
            <Form.Item label={translate("fields.relatedItem", "Related Item")}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "8px 12px",
                  border: "1px solid #d9d9d9",
                  borderRadius: 6,
                  backgroundColor: "#fafafa",
                }}
              >
                <Space>
                  <SelectOutlined />
                  <Text strong>
                    {selectedItem.keyedName ||
                      selectedItem.name ||
                      selectedItem.id}
                  </Text>
                  {selectedItem.itemNumber && (
                    <Text type="secondary">({selectedItem.itemNumber})</Text>
                  )}
                </Space>
                <Button
                  type="link"
                  size="small"
                  onClick={() => {
                    setIsModalOpen(false);
                    setIsPickerOpen(true);
                  }}
                >
                  {translate("buttons.change", "Change")}
                </Button>
              </div>
            </Form.Item>
          )}

          {/* Render all editable fields dynamically from metadata */}
          {editableColumns.map((col) => {
            const fieldName = snakeToCamel(col.name);
            const fieldLabel = col.label;

            // Render different input based on data type
            switch (col.dataType) {
              case "boolean":
                return (
                  <Form.Item
                    key={col.key}
                    name={fieldName}
                    label={fieldLabel}
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                );

              case "integer":
              case "float":
              case "decimal":
                return (
                  <Form.Item key={col.key} name={fieldName} label={fieldLabel}>
                    <InputNumber style={{ width: "100%" }} />
                  </Form.Item>
                );

              case "list":
              case "filter list":
                return (
                  <Form.Item key={col.key} name={fieldName} label={fieldLabel}>
                    <Select
                      allowClear
                      showSearch
                      placeholder={translate("dynamicForm.select", {
                        field: fieldLabel,
                      })}
                      options={
                        col.dataSource && listValues[col.dataSource]
                          ? listValues[col.dataSource].map((opt) => ({
                              label: opt.label,
                              value: opt.value,
                            }))
                          : []
                      }
                      filterOption={(input, option) =>
                        (option?.label?.toString() ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                    />
                  </Form.Item>
                );

              case "text":
                return (
                  <Form.Item key={col.key} name={fieldName} label={fieldLabel}>
                    <Input.TextArea rows={3} />
                  </Form.Item>
                );

              default:
                // string and other types
                return (
                  <Form.Item key={col.key} name={fieldName} label={fieldLabel}>
                    <Input />
                  </Form.Item>
                );
            }
          })}

          {/* Message when no editable properties */}
          {editableColumns.length === 0 && !selectedItem && editingItem && (
            <Text type="secondary">
              {translate(
                "messages.noEditableProperties",
                "This relationship has no editable properties.",
              )}
            </Text>
          )}
        </Form>
      </Modal>

      {/* Create Related Item Modal */}
      {relationship.relatedItemTypeName && (
        <CreateRelatedModal
          itemTypeName={relationship.relatedItemTypeName}
          open={isCreateModalOpen}
          onCancel={() => setIsCreateModalOpen(false)}
          onCreated={handleItemCreated}
          title={`${translate("buttons.createNew", "Create New")} ${
            relationship.relatedItemTypeName
          }`}
        />
      )}

      {/* File Preview Modal */}
      <Modal
        title={previewFile?.name || translate("buttons.view", "View")}
        open={previewVisible}
        onCancel={() => {
          setPreviewVisible(false);
          setPreviewFile(null);
        }}
        footer={[
          <Button
            key="download"
            icon={<DownloadOutlined />}
            onClick={async () => {
              if (previewFile?.id) {
                try {
                  const token = localStorage.getItem("access_token");
                  const response = await fetch(
                    `/api/files/${previewFile.id}/download`,
                    {
                      headers: { Authorization: `Bearer ${token}` },
                    },
                  );
                  if (!response.ok) throw new Error("Download failed");
                  const blob = await response.blob();
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = previewFile.name;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                } catch (error) {
                  console.error("Download failed:", error);
                }
              }
            }}
          >
            {translate("buttons.download", "Download")}
          </Button>,
          <Button
            key="close"
            onClick={() => {
              setPreviewVisible(false);
              setPreviewFile(null);
            }}
          >
            {translate("buttons.close", "Close")}
          </Button>,
        ]}
        width={800}
        centered
      >
        {previewFile && (
          <div style={{ textAlign: "center", minHeight: 300 }}>
            {previewLoading ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: 300,
                }}
              >
                <Spin
                  indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}
                  tip={translate("messages.loading", "Loading...")}
                />
              </div>
            ) : previewBlobUrl && previewFile.mimeType?.startsWith("image/") ? (
              <Image
                src={previewBlobUrl}
                alt={previewFile.name}
                style={{ maxWidth: "100%", maxHeight: "70vh" }}
                preview={false}
              />
            ) : previewBlobUrl && previewFile.mimeType === "application/pdf" ? (
              <iframe
                src={previewBlobUrl}
                title={previewFile.name}
                style={{ width: "100%", height: "70vh", border: "none" }}
              />
            ) : (
              <div
                style={{
                  padding: 40,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 16,
                }}
              >
                <FileOutlined style={{ fontSize: 64, color: "#999" }} />
                <Text type="secondary">
                  {translate(
                    "messages.previewNotSupported",
                    "Preview not available for this file type",
                  )}
                </Text>
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem("access_token");
                      const response = await fetch(
                        `/api/files/${previewFile.id}/download`,
                        {
                          headers: { Authorization: `Bearer ${token}` },
                        },
                      );
                      if (!response.ok) throw new Error("Download failed");
                      const blob = await response.blob();
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = previewFile.name;
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      URL.revokeObjectURL(url);
                    } catch (error) {
                      console.error("Download failed:", error);
                    }
                  }}
                >
                  {translate("buttons.download", "Download")}
                </Button>
              </div>
            )}
          </div>
        )}
      </Modal>
    </>
  );
};

// Default columns when no data analysis is possible
const defaultColumns = [
  {
    title: "ID",
    dataIndex: "id",
    key: "id",
  },
  {
    title: "Related ID",
    dataIndex: "relatedId",
    key: "relatedId",
  },
];

// Helper function to format file sizes
function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

export default RelationshipTable;
